import type { OrderDetail } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router';
import { PAYMENT_STATUS_LABELS } from '@/entities/payment';
import { CompleteBankRefundDialog, ConfirmDepositDialog } from '@/features/order/bank-transfer';
import type { OrderActions } from '@/shared/lib';
import { formatDateTime, formatWon } from '@/shared/lib';
import { ContentCard, detailListStyles as styles } from '@/shared/ui';
import { OrderBankTransferInfo } from './order-bank-transfer-info';

interface OrderPaymentInfoProps {
  readonly order: OrderDetail;
  readonly actions: Pick<OrderActions, 'confirmDeposit' | 'completeBankRefund'>;
  readonly today: string;
  readonly onReload: () => void;
  readonly pending?: boolean;
}

/** 결제 정보 — 결제 상태(사람이 읽는 이름)·결제수단·승인 일시·남은 금액과 결제 상세 링크(환불 이력은 그곳에서 본다). */
export const OrderPaymentInfo = ({
  order,
  actions,
  today,
  onReload,
  pending,
}: OrderPaymentInfoProps): ReactElement => {
  const payment = order.payment;
  const transfer = order.bankTransfer;
  const [dialog, setDialog] = useState<'confirm' | 'refund' | null>(null);

  return (
    <ContentCard
      title="결제 정보"
      actions={
        payment ? (
          <Link
            to={`/payments/${payment.id}`}
            className="text-sm font-medium underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
          >
            결제 상세
          </Link>
        ) : undefined
      }
    >
      {payment ? (
        <>
          <dl className={styles.list}>
            <dt className={styles.term}>결제 상태</dt>
            <dd>{PAYMENT_STATUS_LABELS[payment.status].label}</dd>
            <dt className={styles.term}>결제수단</dt>
            <dd>
              {payment.provider === 'BANK_TRANSFER'
                ? '계좌이체'
                : (payment.method ?? <span className={styles.empty}>—</span>)}
            </dd>
            <dt className={styles.term}>승인 일시</dt>
            <dd>
              {payment.approvedAt ? (
                formatDateTime(payment.approvedAt)
              ) : (
                <span className={styles.empty}>—</span>
              )}
            </dd>
            <dt className={styles.term}>남은 금액</dt>
            <dd className="tabular-nums">{formatWon(payment.balanceAmount)}</dd>
          </dl>
          {transfer && (
            <OrderBankTransferInfo
              transfer={transfer}
              canConfirm={
                !pending && order.status === 'PENDING_PAYMENT' && transfer.status === 'READY'
              }
              canRefund={!pending && transfer.status === 'REFUND_PENDING'}
              onConfirm={() => setDialog('confirm')}
              onRefund={() => setDialog('refund')}
            />
          )}
          {transfer && dialog === 'confirm' && (
            <ConfirmDepositDialog
              orderId={order.id}
              defaultDepositorName={transfer.depositorName}
              today={today}
              onSubmit={actions.confirmDeposit}
              onReload={onReload}
              onClose={() => setDialog(null)}
              pending={pending}
            />
          )}
          {transfer && dialog === 'refund' && (
            <CompleteBankRefundDialog
              orderId={order.id}
              refundAccount={transfer.refundAccount}
              today={today}
              onSubmit={actions.completeBankRefund}
              onReload={onReload}
              onClose={() => setDialog(null)}
              pending={pending}
            />
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">결제 내역 없음</p>
      )}
    </ContentCard>
  );
};
